import { useEffect, useState } from 'react'
import { KalenderPeriod, KalenderToggle, type KalenderCell, type KalenderColumn, type KalenderTone } from 'admination-react'
import { todayIso, formatDatum } from '@/core/lib/dates'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { useApp } from '@/core/state/appStore'
import { ankerBeiWechsel, ankerVerschoben, arbeitstagVon, kalenderSpalten, zeitraumLabel, zeitraumVon, type KalenderModus, type Zeitraum } from '@/core/lib/kalenderZeitraum'
import { getAnwesenheitsliste, type AnwesenheitPerson, type AnwesenheitTag } from '../../api/anwesenheit'

export const LADE_FEHLER = 'Die Anwesenheitsliste konnte nicht geladen werden.'

const HINWEIS: Record<'anwesend' | 'nachgeholt' | 'fehlt' | 'kein_unterricht' | 'au' | 'anruf', string> = {
  anwesend: 'Anwesend',
  nachgeholt: 'Unterschrift nachgeholt',
  fehlt: 'Fehlt',
  kein_unterricht: 'Kein Unterrichtstag',
  au: 'Arbeitsunfähig',
  anruf: 'Entschuldigt, Anruf',
}

const nachholung = ({ grund, erlaeuterung }: AnwesenheitTag): KalenderCell => {
  if (grund === 'unentschuldigt') return { tone: 'fehlt', hint: 'Unentschuldigt gefehlt' }
  if (grund === 'vergessen') return { tone: 'nachgeholt', hint: 'War anwesend, Unterschrift vergessen' }
  return { tone: 'nachholung', hint: grund === 'sonstiges' ? `Sonstiges: ${erlaeuterung ?? ''}` : 'Krank' }
}

export const anwesenheitZelle = (modus: KalenderModus, eintragOeffnen: (person: AnwesenheitPerson, tag: AnwesenheitTag) => void) => (person: AnwesenheitPerson, spalte: KalenderColumn): KalenderCell | null => {
  const tag = person.tage[spalte.key]
  if (!tag || tag.status === 'offen') return null
  if (tag.status === 'nachholung') return nachholung(tag)
  if (tag.status === 'au' || tag.status === 'anruf') return { tone: tag.status, hint: HINWEIS[tag.status], onOpen: () => eintragOeffnen(person, tag) }
  const zeit = tag.zeit
  const hint = HINWEIS[tag.status]
  if (tag.status !== 'anwesend') return { tone: tag.status, hint: zeit ? `${hint} ${zeit}` : hint }
  return modus === 'woche' ? { tone: 'anwesend', hint, time: zeit } : { tone: 'anwesend', hint: zeit ? `${hint} ${zeit}` : hint }
}

export const anwesenheitLegende = (modus: KalenderModus): { tone: KalenderTone; label: string }[] => [
  { tone: 'anwesend', label: modus === 'woche' ? 'Anwesend mit Uhrzeit der Unterschrift' : 'Anwesend (Uhrzeit beim Zeigen)' },
  { tone: 'fehlt', label: modus === 'woche' ? 'Fehlt (keine Unterschrift bis zur Stichzeit der Maßnahme)' : 'Fehlt' },
  { tone: 'nachgeholt', label: 'Unterschrift nachgeholt' },
  { tone: 'kein_unterricht', label: 'Kein Unterrichtstag oder außerhalb der Zuweisung' },
]

export const zuweisungHinweis = ({ von, bis }: Zeitraum) => (person: AnwesenheitPerson): string | null =>
  person.beginn >= von && person.beginn <= bis ? `Zuweisung ab ${formatDatum(person.beginn)}` : null

export function useKalenderZeitraum(start: KalenderModus, startAnker?: string) {
  const [modus, setModus] = useState(start)
  const [anker, setAnker] = useState(startAnker ?? todayIso)
  const zeitraum = zeitraumVon(modus, anker)
  const woche = modus === 'woche'
  const steuerung = <>
    <KalenderPeriod
      label={zeitraumLabel(modus, zeitraum)}
      previousLabel={woche ? 'Vorige Woche' : 'Voriger Monat'}
      nextLabel={woche ? 'Nächste Woche' : 'Nächster Monat'}
      onPrevious={() => setAnker(ankerVerschoben(modus, anker, -1))}
      onNext={() => setAnker(ankerVerschoben(modus, anker, 1))}
    />
    <KalenderToggle
      aria-label="Ansicht"
      options={[{ value: 'woche', label: 'Woche' }, { value: 'monat', label: 'Monat' }] as const}
      value={modus}
      onChange={(ziel) => {
        if (ziel === modus) return
        setAnker(ankerBeiWechsel(ziel, anker, todayIso()))
        setModus(ziel)
      }}
    />
  </>
  return { modus, zeitraum, steuerung, monatsAnker: woche ? arbeitstagVon(anker) : anker }
}

export type KalenderAnsicht = { modus: KalenderModus; zeitraum: Zeitraum; spalten: KalenderColumn[] }
export type ListenStand =
  | { status: 'laedt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'bereit'; personen: AnwesenheitPerson[]; ansicht: KalenderAnsicht }

/** Only the answer for the shown range is ever shown: a new range loads first, and a late answer to an older one is dropped. `zuletzt` keeps the last people for the toolbar. A board reload after a write loads the shown range anew, so a stale row never stands under a finished write. */
export function useAnwesenheitsliste(aktiv: boolean, modus: KalenderModus, zeitraum: Zeitraum): ListenStand & { zuletzt: AnwesenheitPerson[] } {
  const { von, bis } = zeitraum
  const { dashboardVersion } = useApp().state
  const schluessel = `${modus}|${von}|${bis}|${dashboardVersion}`
  const [ergebnis, setErgebnis] = useState<{ schluessel: string; stand: ListenStand } | null>(null)
  const [zuletzt, setZuletzt] = useState<AnwesenheitPerson[]>([])
  useEffect(() => {
    if (!aktiv) return
    let abgebrochen = false
    getAnwesenheitsliste(von, bis)
      .then(({ personen }) => {
        if (abgebrochen) return
        setZuletzt(personen)
        setErgebnis({ schluessel, stand: { status: 'bereit', personen, ansicht: { modus, zeitraum: { von, bis }, spalten: kalenderSpalten(modus, { von, bis }) } } })
      })
      .catch((error: unknown) => {
        if (abgebrochen) return
        console.error('getAnwesenheitsliste() failed:', error)
        setErgebnis({ schluessel, stand: { status: 'fehler', meldung: friendlyErrorMessage(error, LADE_FEHLER) } })
      })
    return () => { abgebrochen = true }
  }, [aktiv, schluessel, modus, von, bis])
  return { ...(ergebnis?.schluessel === schluessel ? ergebnis.stand : { status: 'laedt' }), zuletzt }
}
